import type { Contact, Passenger } from '../../api/types/common';
import type {
  PaymentForm,
  PaymentResponse,
  TransferSearchRequest,
  TransferSearchResponse,
  TransferShoppingCart,
  Vehicle,
} from '../../api/types/transfer';
import {
  allocateTransfer,
  finalizeTransferShopping,
  makeTransferPayment,
  makeTransferPrebooking,
  searchTransfers,
  updateTransferPassengers,
} from '../../api/endpoints/transfer';
import { submitBilling } from '../../api/endpoints/billing';
import type { BillingInformation } from '../../api/types/billing';
import { redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { cartIsPaid, type PersistedBookingFlowConfig } from '../../booking-flow/resume';
import { transferPersistConfig } from './resume';

/**
 * Common flow (migration guide Bölüm 6.1): Search → Allocate → UpdatePassengers →
 * MakePrebooking → FinalizeShopping → MakePayment → Document.
 *
 * NOTE — step order deviates from the doc here: FinalizeShopping was confirmed via
 * curl to require payment already captured on the cart ("Shopping Cart için gerekli
 * ödeme yapılmamış." / PaymentGateError when called before payment), so this config
 * runs MakePayment BEFORE FinalizeShopping. See api/types/transfer.ts header comment
 * for the full empirical trail.
 */
export interface TransferFlowState {
  searchRequest: TransferSearchRequest;
  searchResult?: TransferSearchResponse;
  selectedVehicle?: Vehicle;
  shoppingCartId?: string;
  cart?: TransferShoppingCart;
  passengers: Passenger[];
  contact?: Contact;
  /** Yalnız kurumsal "Onaya Gönder" akışı doldurur — normal transfer formu billing
   *  toplamıyor. Doluysa `runUpdatePassengers` `Billing/Submit`'i zincirler. */
  billingInformation?: BillingInformation;
  /** Kurumsal bütçe/politika dışı transfer yolcu adımı "Onaya Gönder" ile tamamlandı;
   *  `TransferFlowPage` bilgi verip rezervasyon listesine döner. Transient. */
  sentForApproval?: boolean;
  paymentForm?: PaymentForm;
  paymentResponse?: PaymentResponse;
}

export const initialTransferFlowState: TransferFlowState = {
  searchRequest: {
    pickupPointId: 0,
    dropPointId: 0,
    transferDateAndTime: '',
    adtPaxCount: 1,
    chdPaxCount: 0,
    infPaxCount: 0,
  },
  passengers: [],
};

async function runSearch(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  const searchResult = await searchTransfers(state.searchRequest);
  return { searchResult };
}

async function runAllocate(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  if (!state.selectedVehicle) throw new Error('Bir araç seçilmedi.');
  const res = await allocateTransfer({
    productId: state.selectedVehicle.productId,
    quantity: 1,
    shoppingCartId: state.shoppingCartId,
  });
  return { shoppingCartId: res.shoppingCart?.id, cart: res.shoppingCart };
}

/**
 * Same slot-correlation fix as Air (see modules/air/flowConfig.ts's
 * withPaxReferenceIds): Allocate pre-creates one passenger slot per pax, each with a
 * server-generated `paxReferanceId` under `TransferBooking.passengerBreakdowns[].
 * paxReference`. The previous "UpdatePassengers always 500s" conclusion (see
 * api/types/transfer.ts's header) was reached without ever stamping the real id back
 * onto the outgoing passengers — try this path before re-concluding it's a backend bug.
 */
function withPaxReferenceIds(passengers: Passenger[], cart: TransferFlowState['cart']): Passenger[] {
  const idQueueByCode = new Map<string, string[]>();
  for (const booking of cart?.transferBookings ?? []) {
    for (const breakdown of booking.passengerBreakdowns ?? []) {
      const code = breakdown.paxReference?.paxCode;
      const id = breakdown.paxReference?.paxReferanceId;
      if (!code || !id) continue;
      const queue = idQueueByCode.get(code) ?? [];
      if (!queue.includes(id)) queue.push(id);
      idQueueByCode.set(code, queue);
    }
  }

  const cursorByCode = new Map<string, number>();
  return passengers.map((passenger) => {
    const code = passenger.paxType ?? 'ADT';
    const queue = idQueueByCode.get(code);
    if (!queue?.length) return passenger;
    const cursor = cursorByCode.get(code) ?? 0;
    cursorByCode.set(code, cursor + 1);
    const paxReferanceId = queue[cursor];
    return paxReferanceId ? { ...passenger, paxReferanceId } : passenger;
  });
}

async function runUpdatePassengers(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  const res = await updateTransferPassengers({
    shoppingCartId: state.shoppingCartId,
    passengers: withPaxReferenceIds(state.passengers, state.cart),
    contact: state.contact,
  });
  let cart = res.shoppingCart;
  // Kurumsal "Onaya Gönder" akışı fatura bilgisini derler — uçuş akışıyla aynı sıra:
  // UpdatePassengers → Billing/Submit. Normal transfer formu billing toplamadığı için
  // gate'li; bugünkü akış davranışı değişmez.
  if (state.billingInformation && cart?.id) {
    const billingRes = await submitBilling({
      shoppingCartId: cart.id,
      billingInformation: state.billingInformation,
    });
    cart = (billingRes.shoppingCart as typeof cart) ?? cart;
  }
  return { cart };
}

async function runPrebooking(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  const res = await makeTransferPrebooking({ shoppingCartId: state.shoppingCartId });
  // `HasError: false` + `ShoppingCart: null` de dönebiliyor — adımı sessizce geçirmek
  // yerine hata fırlatıp akışı bloke ediyoruz.
  if (!res.shoppingCart) throw new Error('Ön rezervasyon oluşturulamadı — sepet boş döndü.');
  return { cart: res.shoppingCart };
}

async function runPayment(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  if (!state.paymentForm) throw new Error('Ödeme bilgisi girilmedi.');
  // Yenilenmiş/geri dönülmüş bir akışta ödenmiş sepete ikinci kez ödeme denenmesin.
  if (cartIsPaid(state.cart)) throw new Error('Bu rezervasyon zaten ödenmiş.');
  const res = await makeTransferPayment({
    shoppingCartId: state.shoppingCartId,
    paymentForm: state.paymentForm,
  });
  if (redirectToBankIfNeeded(res)) return new Promise<Partial<TransferFlowState>>(() => {});
  return { cart: res.shoppingCart, paymentResponse: res };
}

async function runFinalize(state: TransferFlowState): Promise<Partial<TransferFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı.');
  const res = await finalizeTransferShopping({ shoppingCartId: state.shoppingCartId });
  return { cart: res.shoppingCart };
}

async function runDocument(): Promise<Partial<TransferFlowState>> {
  return {};
}

export const transferFlowConfig: PersistedBookingFlowConfig<TransferFlowState> = {
  id: 'transfer',
  initialState: initialTransferFlowState,
  persist: transferPersistConfig,
  steps: [
    { id: 'search', label: 'Arama', run: runSearch },
    { id: 'allocate', label: 'Rezervasyona Alma', run: runAllocate },
    { id: 'updatePassengers', label: 'Yolcu Bilgileri', run: runUpdatePassengers },
    { id: 'prebook', label: 'Ön Rezervasyon', run: runPrebooking },
    { id: 'payment', label: 'Ödeme', run: runPayment },
    { id: 'finalize', label: 'Kesinleştirme', run: runFinalize },
    { id: 'document', label: 'Belge', run: runDocument },
  ],
};
